import React, { useState, useMemo } from 'react';
import DimensionHeader from '../../common/DimensionHeader';
import CollapsibleSection from './CollapsibleSection';
import { AnimatedNumber, AnimatedCard, useAnimateOnMount } from '../../common/AnimationUtils';
import { useTheme } from '../../../utils/ThemeSwitcher';
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Info,
  Layers,
  Activity,
  Users,
  BarChart3,
  TrendingDown,
  Zap,
} from 'lucide-react';
import './StraightLiningSection.css';
import CoachingPanel from './CoachingPanel';
import PulseBeacon from './PulseBeacon';

/**
 * StraightLiningSection — dedicated dashboard for the straight-lining detector.
 *
 * Renders results from `check_straight_lining` (backend module). Combines
 * five complementary detection methods (variance, entropy, repetition,
 * IQR, Isolation Forest) into a single severity score per respondent.
 *
 * Visual language mirrors the Sentinel/Gate/Probe Depth dashboards:
 * shared DimensionHeader, CollapsibleSection dividers, MetricCard chrome
 * with severity corner brackets and PulseBeacon, AnimatedCard fade-ins,
 * AnimatedNumber count-ups, animated bar fills.
 */
const StraightLiningSection = ({
  data, dataCollectorId, coachingSuggestions = [], isSidePanelExpanded, isMobile = false,
}) => {
  const { isDarkMode } = useTheme();

  const results          = data?.overall || {};
  const overallStats     = results.overall || {};
  const sevDist          = results.severity_distribution || {};
  const methodBreakdown  = results.method_breakdown || {};
  const columnAnalysis   = results.column_analysis || {};
  const byCollector      = results.by_collector || {};
  const flaggedRows      = results.flagged_rows || [];
  const recommendations  = results.recommendations || [];
  const parameters       = results.parameters || {};

  const [activeTab, setActiveTab]             = useState('summary');
  const [severityFilter, setSeverityFilter]   = useState('__all__');
  const [collectorFilter, setCollectorFilter] = useState('__all__');
  const [rowSearch, setRowSearch]             = useState('');

  const colors = {
    background:    isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg:        isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt:     isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border:        isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight:   isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text:          isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted:     isDarkMode ? '#6B7280' : '#9CA3AF',
    success: '#10B981',
    warning: '#F59E0B',
    error:   '#EF4444',
    critical:'#DC2626',
    accent:  isDarkMode ? '#5f5f5f' : '#a8a8a8',
  };

  // ----- error / empty states -----
  if (results.error) {
    return (
      <div className="sls-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <Info size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>Straight-lining check could not run</h3>
          <p style={emptyTextStyle(colors)}>{results.error}</p>
        </div>
      </div>
    );
  }

  if (!overallStats || overallStats.total_respondents === undefined) {
    return (
      <div className="sls-empty-state" style={emptyWrapStyle}>
        <div style={emptyCardStyle(colors)}>
          <Zap size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={emptyTitleStyle(colors)}>No straight-lining data yet</h3>
          <p style={emptyTextStyle(colors)}>
            Run the Straight-Lining check to see flagged respondents here.
          </p>
        </div>
      </div>
    );
  }

  // ----- derived values -----
  const qualityScore = overallStats.quality_score ?? 100;
  const totalFlags   = overallStats.total_flags   ?? 0;
  const flagRate     = overallStats.flag_rate     ?? 0;
  const totalResp    = overallStats.total_respondents ?? 0;
  const colCount     = overallStats.column_count  ?? 0;

  const severityColor = (sev) =>
    sev === 'critical' ? colors.critical :
    sev === 'high'     ? colors.error    :
    sev === 'moderate' ? colors.warning  :
    sev === 'low'      ? '#FBBF24'       : colors.textMuted;

  const filteredRows = useMemo(() => {
    let rows = flaggedRows;
    if (severityFilter  !== '__all__') rows = rows.filter(r => r.severity === severityFilter);
    if (collectorFilter !== '__all__') rows = rows.filter(r => r.collector_id === collectorFilter);
    if (rowSearch.trim()) {
      const q = rowSearch.trim().toLowerCase();
      rows = rows.filter(r =>
        (r.respondent_id || '').toString().toLowerCase().includes(q) ||
        (r.collector_id  || '').toString().toLowerCase().includes(q) ||
        (r.detected_by   || []).join(',').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [flaggedRows, severityFilter, collectorFilter, rowSearch]);

  const collectorRows = useMemo(() =>
    Object.entries(byCollector)
      .map(([cid, cd]) => ({
        collector: cid,
        flag_count: cd.flag_count ?? 0,
        severity_distribution: cd.severity_distribution || {},
      }))
      .sort((a, b) => b.flag_count - a.flag_count),
    [byCollector]
  );

  // ----------------------------------------------------- shared MetricCard
  const MetricCard = ({ icon: Icon, title, value, subtitle, severity, animDelay = 0 }) => {
    const sevLight = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
    const sevDark  = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
    const iconColor = isDarkMode ? (sevDark || '#60a5fa') : (sevLight || '#2563EB');

    return (
      <AnimatedCard delay={animDelay} translateY={12}>
        <div className="sls-metric-card" style={{
          position: 'relative', padding: '18px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden', fontFamily: "'Outfit', -apple-system, sans-serif",
        }}>
          {severity && (
            <svg width="30" height="30" viewBox="0 0 30 30" style={{
              position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible',
            }}>
              <path d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
                    stroke={iconColor} strokeWidth="2.5" fill="none"
                    strokeLinecap="round" opacity={0.9} />
            </svg>
          )}

          <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '5px',
              background: severity
                ? (isDarkMode ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                              : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
                : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, color: iconColor,
            }}>
              <Icon size={16} strokeWidth={1.75} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
              <div style={{
                fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em',
                textTransform: 'lowercase', color: colors.textMuted, marginBottom: '10px',
              }}>{title}</div>
              <div className="metric-value" style={{
                fontSize: '32px', fontWeight: 500, letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums', color: colors.text,
                lineHeight: 1.05, marginBottom: '6px',
              }}>
                {typeof value === 'number'
                  ? <AnimatedNumber value={value} duration={800} />
                  : value}
              </div>
              <div style={{
                fontSize: '11.5px', fontWeight: 400, color: colors.textMuted, lineHeight: 1.4,
              }}>{subtitle}</div>
            </div>
            <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
          </div>
        </div>
      </AnimatedCard>
    );
  };

  // ----------------------------------------------------- horizontal bar
  const HBar = ({ value, max, color, delay = 0 }) => {
    const ready = useAnimateOnMount(300 + delay);
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (
      <div style={{
        width: '100%', height: '8px',
        background: isDarkMode ? '#2A2A2E' : '#E5E7EB',
        borderRadius: '4px', overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: ready ? `${pct}%` : '0%',
          background: color,
          borderRadius: '4px',
          transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      </div>
    );
  };

  // ============================================================ tab: SUMMARY
  const renderSummary = () => (
    <div>
      <CollapsibleSection
        title="Key Metrics"
        isDarkMode={isDarkMode}
        colors={colors}
        badge={
          totalFlags > 0 ? (
            <span style={{
              padding: '2px 8px', borderRadius: '10px',
              backgroundColor: '#EF444420', color: '#EF4444',
              fontSize: '11px', fontWeight: 600,
            }}>
              {totalFlags} flag{totalFlags === 1 ? '' : 's'}
            </span>
          ) : null
        }
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}>
          <MetricCard
            animDelay={0}
            icon={Users}
            title="Respondents Analyzed"
            value={totalResp}
            subtitle={`${colCount} scale column${colCount === 1 ? '' : 's'} checked`}
          />
          <MetricCard
            animDelay={80}
            icon={AlertTriangle}
            title="Flagged Respondents"
            value={totalFlags}
            subtitle={`${flagRate.toFixed(1)}% of respondents`}
            severity={
              flagRate > 20 ? 'critical' :
              flagRate > 10 ? 'warning'  : null
            }
          />
          <MetricCard
            animDelay={160}
            icon={CheckCircle}
            title="Quality Score"
            value={`${qualityScore}%`}
            subtitle={
              qualityScore >= 90 ? 'Excellent' :
              qualityScore >= 70 ? 'Good'      :
              qualityScore >= 50 ? 'Fair'      : 'Poor'
            }
            severity={
              qualityScore < 50 ? 'critical' :
              qualityScore < 70 ? 'warning'  : null
            }
          />
          <MetricCard
            animDelay={240}
            icon={TrendingDown}
            title="Mean Severity Score"
            value={(overallStats.mean_severity_score ?? 0).toFixed(2)}
            subtitle="0 = clean, 1 = severe"
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Detection Methods" isDarkMode={isDarkMode} colors={colors}>
        <AnimatedCard delay={0} translateY={12}>
          <div className="sls-method-card" style={{
            padding: '20px', backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`, borderRadius: '12px',
          }}>
            <div style={subheaderStyle(colors)}>per-method flag counts</div>
            <div style={cardTitleStyle(colors)}>How each method contributed</div>
            <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '16px', lineHeight: 1.5 }}>
              A respondent is flagged when at least one method assigns a high score.
              Method counts may exceed total flags because the same respondent can trip multiple methods.
            </div>
            {Object.keys(methodBreakdown).length === 0 ? (
              <div style={{ fontSize: '13px', color: colors.textMuted, padding: '20px 0' }}>
                No method data available
              </div>
            ) : (
              <>
                {(() => {
                  const maxCount = Math.max(
                    ...Object.values(methodBreakdown).map(m => m.flagged_count ?? 0),
                    1
                  );
                  return Object.entries(methodBreakdown).map(([method, m], i) => {
                    const count = m.flagged_count ?? 0;
                    const c = count >= maxCount * 0.7 ? colors.error
                            : count >= maxCount * 0.4 ? colors.warning
                            :                            colors.textSecondary;
                    return (
                      <div key={method} style={{
                        display: 'grid', gridTemplateColumns: '160px 1fr 70px',
                        alignItems: 'center', gap: '14px',
                        padding: '10px 0',
                        borderBottom: i < Object.keys(methodBreakdown).length - 1
                          ? `1px solid ${colors.borderLight}` : 'none',
                      }}>
                        <div>
                          <div style={{
                            fontSize: '13px', fontWeight: 500, color: colors.text,
                            fontFamily: 'ui-monospace, monospace',
                          }}>
                            {method}
                          </div>
                          <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>
                            {m.description}
                          </div>
                        </div>
                        <HBar value={count} max={maxCount} color={c} delay={i * 60} />
                        <div style={{
                          textAlign: 'right', color: c,
                          fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '14px',
                        }}>
                          <AnimatedNumber value={count} duration={800} delay={i * 60} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </>
            )}
          </div>
        </AnimatedCard>
      </CollapsibleSection>

      <CollapsibleSection title="Severity & Top Collectors" isDarkMode={isDarkMode} colors={colors}>
        <div className="sls-analysis-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '16px',
        }}>
          {/* Severity distribution */}
          <AnimatedCard delay={0} translateY={12}>
            <div className="sls-severity-card" style={{
              padding: '20px', backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`, borderRadius: '12px',
            }}>
              <div style={subheaderStyle(colors)}>severity distribution</div>
              <div style={cardTitleStyle(colors)}>Flagged respondents by severity</div>
              {totalFlags === 0 ? (
                <div style={{ fontSize: '13px', color: colors.success, padding: '20px 0' }}>
                  ✓ No flagged respondents
                </div>
              ) : (
                <>
                  {['critical', 'high', 'moderate', 'low'].map((sev, i) => {
                    const count = sevDist[sev] ?? 0;
                    const c = severityColor(sev);
                    return (
                      <div key={sev} style={{
                        display: 'grid', gridTemplateColumns: '90px 1fr 50px',
                        alignItems: 'center', gap: '12px',
                        padding: '10px 0',
                        borderBottom: i < 3 ? `1px solid ${colors.borderLight}` : 'none',
                      }}>
                        <span style={pillStyle(c)}>{sev}</span>
                        <HBar value={count} max={Math.max(totalFlags, 1)} color={c} delay={i * 60} />
                        <div style={{
                          textAlign: 'right', color: count > 0 ? c : colors.textMuted,
                          fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: '14px',
                        }}>
                          <AnimatedNumber value={count} duration={700} delay={i * 60} />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </AnimatedCard>

          {/* Top flagged collectors */}
          <AnimatedCard delay={120} translateY={12}>
            <div className="sls-top-issues" style={{
              padding: '20px', backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`, borderRadius: '12px',
            }}>
              <div style={subheaderStyle(colors)}>top issues</div>
              <div style={cardTitleStyle(colors)}>Top Collector Issues</div>
              {collectorRows.length === 0 ? (
                <div style={{ fontSize: '13px', color: colors.textMuted, padding: '20px 0' }}>
                  No collector data available
                </div>
              ) : (
                <>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr auto',
                    fontSize: '11px', color: colors.textMuted, marginBottom: '8px',
                    paddingBottom: '8px', borderBottom: `1px solid ${colors.borderLight}`,
                  }}>
                    <div>collector</div>
                    <div style={{ textAlign: 'right' }}>flags</div>
                  </div>
                  {collectorRows.slice(0, 5).map((c, i) => (
                    <div key={i} className="sls-row" style={{
                      display: 'grid', gridTemplateColumns: '1fr auto',
                      padding: '8px 0', fontSize: '12.5px',
                    }}>
                      <div style={{ color: colors.text }}>{c.collector}</div>
                      <div style={{
                        textAlign: 'right', color: colors.error,
                        fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                      }}>
                        <AnimatedNumber value={c.flag_count} duration={700} delay={i * 60} />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </AnimatedCard>
        </div>
      </CollapsibleSection>

      {recommendations.length > 0 && (
        <CollapsibleSection title="Recommendations" isDarkMode={isDarkMode} colors={colors}>
          <AnimatedCard delay={0} translateY={12}>
            <div style={{
              padding: '16px 20px', backgroundColor: colors.cardBg,
              border: `1px solid ${colors.border}`, borderRadius: '12px',
            }}>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: colors.text, lineHeight: 1.7 }}>
                {recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </AnimatedCard>
        </CollapsibleSection>
      )}

      <CoachingPanel
        suggestions={coachingSuggestions}
        detectorSource="straight_lining"
        isMobile={isMobile}
      />
    </div>
  );

  // ============================================================ tab: ROWS
  const renderRows = () => (
    <div>
      <div style={{
        display: 'flex', gap: '10px', flexWrap: 'wrap',
        alignItems: 'center', marginBottom: '14px',
      }}>
        <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
          <option value="__all__">All severities ({flaggedRows.length})</option>
          {['critical', 'high', 'moderate', 'low'].map(sev => (
            <option key={sev} value={sev}>
              {sev} ({sevDist[sev] ?? 0})
            </option>
          ))}
        </select>

        {Object.keys(byCollector).length > 0 && (
          <select value={collectorFilter} onChange={(e) => setCollectorFilter(e.target.value)} style={selectStyle(isDarkMode, colors)}>
            <option value="__all__">All collectors</option>
            {Object.keys(byCollector).map(cid => (
              <option key={cid} value={cid}>
                {cid} ({byCollector[cid].flag_count ?? 0})
              </option>
            ))}
          </select>
        )}

        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '240px' }}>
          <Search size={14} style={searchIconStyle(colors)} />
          <input
            type="text"
            placeholder="Search by respondent, collector, method..."
            value={rowSearch}
            onChange={(e) => setRowSearch(e.target.value)}
            style={searchInputStyle(colors)}
          />
        </div>

        <div style={{ fontSize: '12px', color: colors.textMuted, marginLeft: 'auto' }}>
          Showing {filteredRows.length} of {flaggedRows.length}
        </div>
      </div>

      <div style={tableWrapStyle(colors)}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle(isDarkMode)}>
              <th style={th(colors)}>Respondent</th>
              <th style={th(colors)}>Collector</th>
              <th style={th(colors)}>Severity</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Score</th>
              <th style={th(colors)}>Detected by</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={5} style={emptyRowStyle(colors)}>No flagged rows match the filters.</td></tr>
            ) : (
              filteredRows.map((r, i) => (
                <tr key={i} className="sls-table-row" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12.5px' }}>
                    {r.respondent_id || `row ${r.row_index}`}
                  </td>
                  <td style={td(colors)}>{r.collector_id || '—'}</td>
                  <td style={td(colors)}>
                    <span style={pillStyle(severityColor(r.severity))}>{r.severity}</span>
                  </td>
                  <td style={{
                    ...tdRight(colors),
                    color: severityColor(r.severity),
                    fontWeight: 600,
                  }}>
                    {(r.score ?? 0).toFixed(2)}
                  </td>
                  <td style={{ ...td(colors), fontSize: '12px', color: colors.textSecondary }}>
                    {(r.detected_by || []).join(', ') || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================ tab: COLLECTORS
  const renderCollectors = () => (
    <div>
      <div style={tableWrapStyle(colors)}>
        <table style={tableStyle}>
          <thead>
            <tr style={theadRowStyle(isDarkMode)}>
              <th style={th(colors)}>Collector</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Total flags</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Critical</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>High</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Moderate</th>
              <th style={{ ...th(colors), textAlign: 'right' }}>Low</th>
            </tr>
          </thead>
          <tbody>
            {collectorRows.length === 0 ? (
              <tr><td colSpan={6} style={emptyRowStyle(colors)}>No collector breakdown available.</td></tr>
            ) : (
              collectorRows.map((c, i) => {
                const sd = c.severity_distribution || {};
                return (
                  <tr key={i} className="sls-table-row" style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={td(colors)}>{c.collector}</td>
                    <td style={{
                      ...tdRight(colors),
                      color: c.flag_count > 0 ? colors.error : colors.textMuted,
                      fontWeight: 600,
                    }}>
                      {c.flag_count}
                    </td>
                    <td style={{ ...tdRight(colors), color: (sd.critical ?? 0) > 0 ? colors.critical : colors.textMuted }}>
                      {sd.critical ?? 0}
                    </td>
                    <td style={{ ...tdRight(colors), color: (sd.high ?? 0) > 0 ? colors.error : colors.textMuted }}>
                      {sd.high ?? 0}
                    </td>
                    <td style={{ ...tdRight(colors), color: (sd.moderate ?? 0) > 0 ? colors.warning : colors.textMuted }}>
                      {sd.moderate ?? 0}
                    </td>
                    <td style={{ ...tdRight(colors), color: colors.textMuted }}>
                      {sd.low ?? 0}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ============================================================ tab: COLUMNS
  const renderColumns = () => {
    const colEntries = Object.entries(columnAnalysis);
    return (
      <div>
        <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '12px' }}>
          For each scale column, how many of the flagged respondents had a non-null value there.
          Columns where flagged respondents cluster are likely the source of the flatlining behavior
          (often long Likert grids near the end of the form).
        </div>
        <div style={tableWrapStyle(colors)}>
          <table style={tableStyle}>
            <thead>
              <tr style={theadRowStyle(isDarkMode)}>
                <th style={th(colors)}>Column</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>Flagged respondents w/ value</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>Total non-null</th>
                <th style={{ ...th(colors), textAlign: 'right' }}>Concentration</th>
              </tr>
            </thead>
            <tbody>
              {colEntries.length === 0 ? (
                <tr><td colSpan={4} style={emptyRowStyle(colors)}>No column-level breakdown available.</td></tr>
              ) : (
                colEntries.map(([col, ca], i) => {
                  const fc = ca.flagged_count ?? 0;
                  const tc = ca.total_count   ?? 0;
                  const conc = totalFlags > 0 ? (fc / totalFlags) * 100 : 0;
                  return (
                    <tr key={i} className="sls-table-row" style={{ borderTop: `1px solid ${colors.border}` }}>
                      <td style={{ ...td(colors), fontFamily: 'ui-monospace, monospace', fontSize: '12.5px' }}>
                        {col}
                      </td>
                      <td style={{
                        ...tdRight(colors),
                        color: fc > 0 ? colors.error : colors.textMuted,
                        fontWeight: 600,
                      }}>
                        {fc}
                      </td>
                      <td style={tdRight(colors)}>{tc}</td>
                      <td style={tdRight(colors)}>{conc.toFixed(0)}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ----- tabs -----
  const tabs = [
    { id: 'summary',    label: 'Summary',    icon: BarChart3 },
    { id: 'rows',       label: 'Flagged Rows', icon: AlertTriangle, count: flaggedRows.length },
    { id: 'collectors', label: 'By Collector', icon: Users, count: collectorRows.length },
    { id: 'columns',    label: 'By Column',  icon: Layers, count: Object.keys(columnAnalysis).length },
  ];

  return (
    <div className="sls-section">
      <DimensionHeader
        slug="straight-lining"
        title="Straight-Lining"
        description='Detects respondents whose answers across Likert / numeric scale columns look like flatlining, repetition, or other inattentive patterns. Combines five complementary methods (variance, entropy, repetition, IQR, Isolation Forest) into a single severity score per respondent.'
        rememberCollapseKey="straight-lining"
        stat={{
          score: qualityScore,
          scoreLabel: 'quality score',
          unit: '%',
        }}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />

      <div style={{ minHeight: '600px', marginTop: '16px' }}>
        {activeTab === 'summary'    && renderSummary()}
        {activeTab === 'rows'       && renderRows()}
        {activeTab === 'collectors' && renderCollectors()}
        {activeTab === 'columns'    && renderColumns()}
      </div>
    </div>
  );
};

// ----------------------------------------------------------- shared styles
const emptyWrapStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  minHeight: '400px', padding: '32px',
};
const emptyCardStyle = (colors) => ({
  textAlign: 'center', padding: '32px', borderRadius: '16px',
  backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`,
});
const emptyTitleStyle = (colors) => ({
  fontSize: '16px', fontWeight: 600, color: colors.text, marginBottom: '8px',
});
const emptyTextStyle = (colors) => ({ fontSize: '13px', color: colors.textSecondary });

const subheaderStyle = (colors) => ({
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em',
  color: colors.textMuted, marginBottom: '4px', fontWeight: 500,
});
const cardTitleStyle = (colors) => ({
  fontSize: '14px', fontWeight: 600, color: colors.text, marginBottom: '14px',
});

const th = (colors) => ({
  padding: '12px 14px', textAlign: 'left', fontWeight: 600,
  color: colors.text, fontSize: '12px',
  textTransform: 'uppercase', letterSpacing: '0.04em',
});
const td = (colors) => ({ padding: '11px 14px', color: colors.text });
const tdRight = (colors) => ({
  padding: '11px 14px', textAlign: 'right',
  fontVariantNumeric: 'tabular-nums', color: colors.text,
});

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const tableWrapStyle = (colors) => ({
  backgroundColor: colors.cardBg,
  border: `1px solid ${colors.border}`,
  borderRadius: '12px',
  overflow: 'hidden',
});
const theadRowStyle = (isDarkMode) => ({
  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
});
const emptyRowStyle = (colors) => ({
  padding: '32px', textAlign: 'center', color: colors.textMuted,
});

const pillStyle = (color) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
  fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
  background: `${color}22`, color,
});

const searchIconStyle = (colors) => ({
  position: 'absolute', left: '10px', top: '50%',
  transform: 'translateY(-50%)', color: colors.textMuted,
});
const searchInputStyle = (colors) => ({
  width: '100%', padding: '8px 12px 8px 32px',
  borderRadius: '8px', border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  fontFamily: 'inherit',
});

const selectStyle = (isDarkMode, colors) => ({
  padding: '8px 12px', borderRadius: '8px',
  border: `1px solid ${colors.border}`,
  background: colors.cardBg, color: colors.text, fontSize: '13px',
  cursor: 'pointer', fontFamily: 'inherit', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='${isDarkMode ? '%239CA3AF' : '%236B7280'}' d='M6 9L1 4h10z'/></svg>")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '28px',
});

export default StraightLiningSection;
